import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import CosmicBackground from '../components/CosmicBackground';
import ToolsNavigation from '../components/ToolsNavigation';
import ToolCard from '../components/ToolCard';
import { getToolsByCategory } from '../data/toolsData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTool, FiZap } = FiIcons;

const ToolsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  
  const tools = getToolsByCategory(activeCategory);
  const freeTools = tools.filter(tool => !tool.isPremium);
  const premiumTools = tools.filter(tool => tool.isPremium);

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-12 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center space-x-2 bg-cyan-900/30 px-4 py-2 rounded-full border border-cyan-500/30 mb-6">
              <SafeIcon icon={FiTool} className="text-cyan-400" />
              <span className="text-sm font-poppins text-cyan-200">45+ AI Tools</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-orbitron font-bold mb-6 leading-tight">
              <span className="clarity-gradient">AI-Powered Tools</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
              Everything you need to build, market, and grow your business. From content creation to automation - all powered by AI.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tools Navigation */}
      <section className="pb-8 relative z-10">
        <div className="container mx-auto px-6">
          <ToolsNavigation 
            activeCategory={activeCategory}
            setActiveCategory={setActiveCategory}
            viewMode={viewMode}
            setViewMode={setViewMode}
          />
        </div>
      </section>

      {/* Free Tools Section */}
      {freeTools.length > 0 && (
        <section className="py-12 relative z-10">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-white text-sm" />
                </div>
                <h2 className="text-2xl md:text-3xl font-orbitron font-bold text-white">
                  Free Tools
                </h2>
              </div>
              <p className="text-gray-300 font-poppins">
                Get started with our always-free tools. No credit card required.
              </p>
            </motion.div>

            <div className={`${
              viewMode === 'grid' 
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-4'
            }`}>
              {freeTools.map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} viewMode={viewMode} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Premium Tools Section */}
      {premiumTools.length > 0 && (
        <section className="py-12 relative z-10">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-white text-sm" />
                </div>
                <h2 className="text-2xl md:text-3xl font-orbitron font-bold text-white">
                  Premium Tools
                </h2>
              </div>
              <p className="text-gray-300 font-poppins">
                Unlock advanced features and unlimited usage with our premium tools.
              </p>
            </motion.div>

            <div className={`${
              viewMode === 'grid' 
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' 
                : 'space-y-4'
            }`}>
              {premiumTools.map((tool, index) => (
                <ToolCard key={tool.id} tool={tool} viewMode={viewMode} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center bg-gradient-to-r from-cyan-900/40 to-blue-900/40 p-12 rounded-3xl border border-cyan-500/30 backdrop-blur-sm max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
              Ready to <span className="clarity-gradient">Supercharge</span> Your Business?
            </h2>
            <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
              Join thousands of entrepreneurs using our AI tools to build, market, and scale their businesses.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button"
              >
                Try Free Tools
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-cyan-500 text-cyan-300 px-8 py-4 rounded-full font-poppins font-semibold text-lg hover:bg-cyan-500/10 transition-colors"
              >
                Upgrade to Premium
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ToolsPage;